import React, { createContext, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Toast, ToastMessage } from '../ui';

interface ToastApi {
  saved: (message?: string, action?: ToastMessage['action']) => void;
  failed: (message: string, retry?: () => void) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** One toast for the whole page, portalled outside the app root so it stays usable while a drawer is open. */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const api = useMemo<ToastApi>(() => ({
    saved: (message = 'Saved.', action) => setToast({ type: 'success', message, action }),
    failed: (message, retry) => setToast({
      type: 'error', message, action: retry ? { label: 'Try again', onClick: retry } : undefined,
    }),
    info: (message) => setToast({ type: 'info', message }),
  }), []);
  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <Toast toast={toast} onDismiss={() => setToast(null)} autoDismissMs={toast?.type === 'error' ? 9000 : 5500} />,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastApi => {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside ToastProvider');
  return api;
};
